import moment from 'moment';
import { Group, Loader, Menu, Table } from '@mantine/core';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  fetchExistencesMSReport,
  fetchHelpdeskEquipmentList,
  IHelpdeskEquipmentResponse
} from '../../../../libraries/helpdesk/equipments';
import { LoginState } from '../../../client-redux/login/reducer';
import { AppState } from '../../../client-redux/reducers';
import CustomModal from '../../../components/custom-modal';
import PaginationComponent from '../../../components/pagination';
import ShowEquipment from '../../../components/show-equipment';
import { useFitTableRows } from '../../../components/util/fit-table-rows-hook';
import Header from '../../../components/util/header';
import { Language } from '../../../languages/languageHandler';
import { formatDateLabel, formatDateTimeLabel, toMoment } from '../../../utils/date';
import AutorizeInitializationModal from '../../components/autorize-initialization-modal';
import HelpdeskFilterBar, { HelpdeskFilterBarFieldsData } from '../../components/helpdesk-filterbar';
import { detectContractState } from '../../utils';
import { TICKETSTATUS } from '../ticket-list';
import './styles.scss';

interface HideColumns {
  posid: boolean;
  maintenance: boolean;
  agent: boolean;
  lastCommDate: boolean;
  agreement: boolean;
  licence: boolean;
}

const HelpdeskEquipment: React.FC = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const loginInfo = useSelector<AppState, LoginState>(state => state.login);
  const controller = new AbortController();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const [openDetails, setOpenDetails] = useState<boolean>(false);
  const [activeRow, setActiveRow] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [totalRowsResponse, setTotalRowsResponse] = useState<number | undefined>();
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: 1,
    minHeightForDynamic: 400
  });
  const [list, setList] = useState<IHelpdeskEquipmentResponse>();
  const [noResults, setNoResults] = useState<boolean>(true);
  const [statusOperCode, setStatusOperCode] = useState<number | undefined>();
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [successMessage /* setSuccessMessage */] = useState('');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const [menuOpened, setMenuOpened] = useState(false);
  const [menuCoords, setMenuCoords] = useState<{ x: number; y: number } | null>(null);
  const [contextEid, setContextEid] = useState<number | null>(null);
  const [contextEntid, setContextEntid] = useState<number | null>(null);
  const [contextSid, setContextSid] = useState<number | null>(null);

  const entidParam = searchParams.get('entid');

  const [initModalOpen, setInitModalOpen] = useState(false);
  const selectedEquipment = list?.list.find(eq => eq.eid === contextEid);

  const [currentPage, setCurrentPage] = useState(1);
  const [filterBarData, setFilterBarData] = useState<HelpdeskFilterBarFieldsData>();

  const [hideColumns, setHideColumns] = useState<HideColumns>({
    posid: true,
    maintenance: false,
    agent: false,
    lastCommDate: true,
    agreement: false,
    licence: false
  });

  useEffect(() => {
    if (loginInfo.permissions && loginInfo.permissions.hasHelpdeskAgent()) {
      console.log('User has Helpdesk Agent permissions, adjusting visible columns accordingly.');
      setHideColumns({
        ...hideColumns,
        posid: false,
        maintenance: true,
        agent: true,
        lastCommDate: false,
        agreement: true,
        licence: true
      });
    }
  }, [loginInfo.permissions]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (filterBarData && limit) {
      loadEquipmentList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: HelpdeskFilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadEquipmentList = () => {
    if (!loginInfo.token) {
      return;
    }
    if (loading) {
      setNoResults(false);
      controller.abort();
    }
    setLoading(true);
    const shouldFetchTotalRows = offset === 0;

    fetchHelpdeskEquipmentList(
      controller,
      offset,
      limit,
      shouldFetchTotalRows,
      filterBarData?.entity || Number(entidParam),
      filterBarData?.etid,
      filterBarData?.sn,
      filterBarData?.eid
    )
      .then(response => {
        if (response.statusOper.code === 0) {
          setList(response);
          if (shouldFetchTotalRows && typeof response.totalRows === 'number') {
            setTotalRowsResponse(response.totalRows);
          }
          setNoResults(false);
          setStatusOperCode(response.statusOper.code);
        } else {
          setNoResults(true);
          setStatusOperCode(response.statusOper.code);
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
        } else {
        }
      })
      .finally(() => setLoading(false));
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const getXLSReport = () => {
    fetchExistencesMSReport(
      controller,
      filterBarData?.entity || Number(entidParam),
      filterBarData?.etid,
      filterBarData?.sn,
      filterBarData?.eid
    )
      .then(response => {
        if (response.statusOper.code !== 0) {
          setNoResults(true);
          setErrorMessage(
            response?.statusOper.message || 'An error occurred, but no specific error message is available.'
          );
          setShowErrorDialog(true);
        }
      })
      .catch(() => {
        setShowErrorDialog(true);
      });
  };

  const showEquipment = (selectedEid: number) => {
    setActiveRow(selectedEid);
    setOpenDetails(true);
  };

  const handleRowRightClick = (event: React.MouseEvent, eid: number, entid: number, sn: string, sid: number) => {
    event.preventDefault();
    setContextEid(eid);
    setContextEntid(entid);
    setContextSid(sid);

    const menuWidth = 200;
    const menuHeight = 300;
    const maxX = window.innerWidth - menuWidth;
    const maxY = window.innerHeight - menuHeight;

    const x = event.clientX > maxX ? maxX : event.clientX;
    const y = event.clientY > maxY ? maxY : event.clientY;

    setMenuCoords({ x, y });
    setMenuOpened(true);
  };

  const handleContractNavigation = () => {
    const selectedEquipment = list?.list.find(eq => eq.eid === contextEid);

    if (selectedEquipment?.agreeId) {
      navigate(`/helpdesk/contract/edit/${selectedEquipment.agreeId}`);
    } else {
      setErrorMessage(lang.EQUIPMENT_NO_CONTRACT || 'No agreement associated with this equipment.');
      setShowErrorDialog(true);

      setTimeout(() => setShowErrorDialog(false), 2000);
    }

    setMenuOpened(false);
  };

  const navigateToLocation = () => {
    if (!contextSid || contextSid === 0) {
      setErrorMessage(lang.LOCATION_NOT_DEFINED || 'Location SID is missing. Cannot navigate.');
      setShowErrorDialog(true);

      setTimeout(() => {
        setShowErrorDialog(false);
      }, 2000);
    } else {
      navigate(`/helpdesk/location/edit/${contextEntid}/${contextSid}`);
    }
    setMenuOpened(false);
  };

  const navigateToEntity = () => {
    if (!contextEntid || contextEntid === 0) {
      setErrorMessage(lang.ENTITY_NOT_DEFINED || 'Entity not defined');
      setShowErrorDialog(true);
      setTimeout(() => setShowErrorDialog(false), 2000);
    } else {
      navigate(`/helpdesk/entity/read/${contextEntid}`);
    }
    setMenuOpened(false);
  };

  return (
    <div className="helpdesk-equipments">
      {openDetails && activeRow && lang && (
        <ShowEquipment eid={activeRow} openDetails={openDetails} hideBtn={() => setOpenDetails(false)} lang={lang} />
      )}

      <Header pageTitle={lang.GLOBAL_EQUIPMENTS} />

      <HelpdeskFilterBar
        onSearch={loadEquipmentList}
        onDataChange={handleFilterBarOnDataChange}
        fieldsUsage={{
          entity: loginInfo.permissions.hasHelpdesk(),
          etid: true,
          sn: true,
          eid: true
        }}
        xlsReport={loginInfo.permissions.hasHelpdesk() ? getXLSReport : undefined}
      />

      {initModalOpen && contextEid && (
        <AutorizeInitializationModal
          isOpen={initModalOpen}
          hideBtn={() => setInitModalOpen(false)}
          lang={lang}
          eid={contextEid}
          isInitAccepted={selectedEquipment?.initAccepted || false}
          autorizeInitialization={() => {
            setInitModalOpen(false);
            loadEquipmentList();
          }}
        />
      )}

      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{lang.EQUIPMENT_ID_MIN}</Table.Th>
              <Table.Th>{lang.GLOBAL_TYPE}</Table.Th>
              <Table.Th>SN</Table.Th>
              {!hideColumns.posid && <Table.Th>{'POSID'}</Table.Th>}
              {!hideColumns.maintenance && <Table.Th>{lang.GLOBAL_MAINTENANCE}</Table.Th>}
              {!hideColumns.agent && <Table.Th>{lang.GLOBAL_AGENT}</Table.Th>}
              <Table.Th>{lang.GLOBAL_ENTITY}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_LOCAL}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_COM_STATE}</Table.Th>
              {!hideColumns.lastCommDate && <Table.Th>{lang.EQUIPMENT_COM_DATE_MIN}</Table.Th>}
              <Table.Th>{lang.EQUIPMENT_SOFTWARE_VERSION_MIN}</Table.Th>
              {!hideColumns.agreement && <Table.Th>{lang.GLOBAL_CONTRACT}</Table.Th>}
              {!hideColumns.licence && <Table.Th>{lang.GLOBAL_LICENSE}</Table.Th>}
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={16}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : noResults ? (
              <Table.Tr>
                <Table.Td colSpan={16} style={{ textAlign: 'center' }}>
                  {lang.GLOBAL_NO_RESULTS}
                </Table.Td>
              </Table.Tr>
            ) : (
              list?.list?.map((equipment, index) => (
                <Table.Tr
                  key={index}
                  onClick={() => showEquipment(equipment.eid)}
                  onContextMenu={e => {
                    e.stopPropagation();
                    handleRowRightClick(
                      e,
                      equipment.eid,
                      equipment.entity?.entid || 0,
                      equipment.sn,
                      equipment.sid || 0
                    );
                  }}
                  style={{ cursor: 'context-menu' }}
                >
                  <Table.Td>{equipment.eid}</Table.Td>
                  <Table.Td>
                    {equipment.type && equipment.productCode
                      ? `${equipment.type} / ${equipment.productCode}`
                      : equipment.type || ''}
                  </Table.Td>
                  <Table.Td>{equipment.sn}</Table.Td>
                  {!hideColumns.posid && <Table.Td>{equipment.posid || ''}</Table.Td>}
                  {!hideColumns.maintenance && (
                    <Table.Td>
                      {equipment.maintEntity
                        ? `${equipment.maintEntity.name ?? ''} #${equipment.maintEntity.entid ?? ''}`
                        : ''}
                    </Table.Td>
                  )}
                  {!hideColumns.agent && (
                    <Table.Td>
                      {equipment.maintEntity2
                        ? `${equipment.maintEntity2.name ?? ''} #${equipment.maintEntity2.entid ?? ''}`
                        : ''}
                    </Table.Td>
                  )}
                  <Table.Td>
                    {equipment.entity ? `${equipment.entity.name ?? ''} #${equipment.entity.entid ?? ''}` : ''}
                  </Table.Td>
                  <Table.Td>
                    {equipment.sid && equipment.sidDescription
                      ? `${equipment.sid} - ${equipment.sidDescription}`
                      : equipment.sidDescription || ''}
                  </Table.Td>
                  <Table.Td
                    className={
                      equipment.status !== 'ONLINE' && equipment.status !== 'DEFINED'
                        ? 'out-of-service-text'
                        : 'in-service-text'
                    }
                  >
                    {equipment.status}
                  </Table.Td>
                  {!hideColumns.lastCommDate && <Table.Td>{formatDateTimeLabel(equipment?.lastCommDate)}</Table.Td>}
                  <Table.Td>{equipment.softwareVersion}</Table.Td>
                  {!hideColumns.agreement && (
                    <Table.Td className={equipment.agreeState === 1 ? 'valid-agree-state' : 'invalid-agree-state'}>
                      {detectContractState(equipment.agreeState, lang)}
                    </Table.Td>
                  )}
                  {!hideColumns.licence && (
                    <Table.Td
                      className={
                        toMoment(equipment?.licenceExp).isBefore(moment(), 'day') ? 'expired-license' : 'valid-license'
                      }
                    >
                      {equipment?.licenceExp ? formatDateLabel(equipment?.licenceExp) : ''}
                    </Table.Td>
                  )}
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {menuOpened && menuCoords && loginInfo.permissions.hasHelpdesk() && (
        <Menu
          opened={true}
          onClose={() => setMenuOpened(false)}
          withinPortal
          styles={{
            dropdown: {
              position: 'fixed',
              top: `${menuCoords.y}px`,
              left: `${menuCoords.x}px`
            }
          }}
        >
          <Menu.Target>
            <div
              style={{
                position: 'absolute',
                top: `${menuCoords.y}px`,
                left: `${menuCoords.x}px`,
                width: 1,
                height: 1
              }}
            />
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Item onClick={() => navigate({ pathname: `/helpdesk/events`, search: `?eid=${contextEid}` })}>
              {lang.EVENT_LOG_CONSULT}
            </Menu.Item>
            <Menu.Item onClick={() => navigate(`/helpdesk/equipment/registration/${contextEid}`)}>
              {lang.EQUIPMENT_REGISTER}
            </Menu.Item>
            <Menu.Item onClick={navigateToEntity}>{lang.GLOBAL_ENTITY}</Menu.Item>

            <Menu.Item onClick={navigateToLocation}>{lang.GLOBAL_LOCATION}</Menu.Item>
            <Menu.Item
              onClick={() =>
                navigate({
                  pathname: '/helpdesk/devices',
                  search: `?eid=${contextEid}`
                })
              }
            >
              {lang.GLOBAL_DEVICES}
            </Menu.Item>
            <Menu.Item onClick={handleContractNavigation}>{lang.EQUIPMENT_CONTRACT}</Menu.Item>
            <Menu.Item
              onClick={() =>
                navigate({
                  pathname: `/helpdesk/ticket/new/`,
                  search: `eid=${contextEid}`
                })
              }
            >
              {lang.EQUIPMENT_CREATE_TICKET}
            </Menu.Item>

            <Menu.Item
              onClick={() =>
                navigate({
                  pathname: '/helpdesk/tickets',
                  search: `?eid=${contextEid}&ticketStatus=${TICKETSTATUS.ALL}`
                })
              }
            >
              {lang.EQUIPMENT_CONSULT_TICKET}
            </Menu.Item>

            <Menu.Item
              onClick={() =>
                navigate({
                  pathname: '/helpdesk/auditLogs',
                  search: `?eid=${contextEid}`
                })
              }
            >
              {lang.GLOBAL_AUDITLOG}
            </Menu.Item>
            <Menu.Item
              onClick={() =>
                navigate({
                  pathname: '/helpdesk/syncCommands',
                  search: `?eid=${contextEid}`
                })
              }
            >
              {lang.GLOBAL_SYNC_COMMANDS}
            </Menu.Item>
            <Menu.Item onClick={() => setInitModalOpen(true)}>{lang.EQUIPMENT_AUTHORIZE_INITIATION}</Menu.Item>
          </Menu.Dropdown>
        </Menu>
      )}

      <Group>
        <PaginationComponent
          total={totalRowsResponse}
          statusOperCode={statusOperCode}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>

      <CustomModal
        opened={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        message={successMessage}
        backgroundColor="#3BDF70"
        textColor="black"
      />
      <CustomModal
        opened={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        message={errorMessage}
        backgroundColor="#E3353F"
        textColor="white"
      />
    </div>
  );
};

export default HelpdeskEquipment;
